import { isInboxMsChat } from './inboxMsChatPlatform';

/**
 * Plataformas de hilos de comentarios: quedan fuera del selector de canales porque
 * un customer puede tener decenas (una por post) y no son "otro canal" del contacto.
 */
export const COMMENT_PLATFORMS = new Set([
  'facebook_comments',
  'instagram_comments',
  'tik_tok_comments'
]);

/**
 * @param {Object} chat - Item `{ id, type, attributes }` de la API del inbox MS.
 * @returns {number} Timestamp del último mensaje, o 0 si no es una fecha válida.
 */
function lastMessageTimestamp(chat) {
  const timestamp = new Date(chat?.attributes?.recent_message_date).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

/**
 * Filtra los chats del microservicio de un customer para dejar solo los que puede
 * mostrar el selector de canales, del más reciente al más antiguo.
 *
 * Incluye el chat abierto: el selector lo pinta como canal activo, igual que el
 * diseño de referencia. Quien consume decide cuál es el actual.
 *
 * Descarta:
 * 1. Los hilos de comentarios.
 * 2. Las plataformas que ese retailer todavía no tiene migradas al MS.
 *
 * El punto 2 usa `isInboxMsChat` —flag de rollout por retailer más plataforma canónica
 * del MS— y no el set estático del contrato HTTP, porque es el mismo discriminador con
 * el que rutea todo el write-path (enviar, marcar leído, estado, agente, notas). Ofrecer
 * un chat que ese discriminador da por no migrado dejaría al usuario leyendo del MS y
 * escribiendo al monolito con un ObjectId que el monolito no conoce.
 *
 * @param {Array<Object>} chats - Items crudos de `GET /chats/inbox?customer_id=...`.
 * @returns {Array<Object>} Chats mostrables, ordenados por fecha del último mensaje.
 */
export function selectCustomerChannelChats(chats) {
  if (!Array.isArray(chats)) return [];

  return chats
    .filter((chat) => {
      const platform = chat?.attributes?.platform;
      if (COMMENT_PLATFORMS.has(platform)) return false;

      return isInboxMsChat(platform);
    })
    .sort((a, b) => lastMessageTimestamp(b) - lastMessageTimestamp(a));
}

export default selectCustomerChannelChats;
